import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useModalStore } from '../../../store/portal/modalStore';
import { getModalMessage } from '../../../constants/modalMessages';
import TagStationTagRow from '../TagStationTagRow';
import UniversalIconBtn from '../../headless/button/UniversalIconBtn';
import belowIcon from '../../../../asset/icon/below.png';
import { TagStationActions } from '../TagManagementTypes';

interface TagStationModalMobileProps extends TagStationActions {
  setIsUserInput: (isUserInput: boolean) => void;
}

const TagStationModalMobile: React.FC<TagStationModalMobileProps> = ({
  tags,
  loadTags,
  loadKey,
  updateTag,
  deleteTag,
  getDeleteConfirmMessage,
  setIsUserInput,
}) => {
  const { clearModal } = useModalStore();
  const [dirtyByTagId, setDirtyByTagId] = useState<Record<number, true>>({});

  const handleRowDirtyChange = useCallback((tagId: number, dirty: boolean) => {
    setDirtyByTagId((prev) => {
      const alreadyDirty = !!prev[tagId];
      if (alreadyDirty === dirty) return prev;
      const next = { ...prev };
      if (dirty) next[tagId] = true;
      else delete next[tagId];
      return next;
    });
  }, []);

  const handleClose = () => {
    clearModal(400);
  };

  useEffect(() => {
    setIsUserInput(Object.keys(dirtyByTagId).length > 0);
  }, [dirtyByTagId, setIsUserInput]);

  useEffect(() => {
    return () => setIsUserInput(false);
  }, [setIsUserInput]);

  useEffect(() => {
    if (!loadKey) return;
    void loadTags();
    // The adapter owns the request function; rerun only when its stable scope changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey]);

  return (
    <TagStationComp>
      <TitleWrapper>
        <TagStationTitle>{getModalMessage('TAG_STATION_TITLE')}</TagStationTitle>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          backgroundImg={belowIcon}
          onClick={handleClose}
        />
      </TitleWrapper>
      {tags.length === 0 && <EmptyTagContainer>{getModalMessage('TAG_STATION_EMPTY')}</EmptyTagContainer>}
      {tags.length > 0 && (
        <TagListScroll>
          {tags.map((tag) => (
            <TagStationTagRow
              key={tag.id}
              tag={tag}
              updateTag={updateTag}
              deleteTag={deleteTag}
              getDeleteConfirmMessage={getDeleteConfirmMessage}
              onDirtyChange={handleRowDirtyChange}
            />
          ))}
        </TagListScroll>
      )}
    </TagStationComp>
  );
};

const TagStationComp = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  max-height: calc(95vh - 40px);
  max-height: calc(95dvh - 40px);
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const TagStationTitle = styled.h2`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;



const TagListScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 6px;
  padding-bottom: 12px;
`;

const EmptyTagContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_05};
`;

export default TagStationModalMobile;
